import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Bridge to the Spark Boards desktop shell's self-hosted OTA updater. Only meaningful inside the desktop
 * app (Tauri) — in a browser `isDesktop` is false and callers render nothing.
 *
 * The shell injects a tiny bridge before our scripts:
 *   window.__SPARK_DESKTOP__          = true
 *   window.__SPARK_DESKTOP_VERSION__  = "1.1.1"
 *   window.__sparkDesktop.update(action, value)   // action: 'check' | 'now' | 'set-auto'
 * `update(...)` signals Rust over a same-origin sentinel navigation (the __TAURI__ event/invoke bridge
 * can't reach Rust from this remote origin — its ipc:// fetch is CSP/mixed-content blocked). There is no
 * reverse channel, so the auto-update preference is kept in localStorage (mirrored to the shell) and
 * "check/update" is optimistic: when a newer build exists the shell installs it and RELAUNCHES the app.
 */

interface DesktopBridge {
  version?: string;
  update?: (action: string, value?: string | boolean) => void;
}
interface DesktopWindow {
  __SPARK_DESKTOP__?: boolean;
  __SPARK_DESKTOP_VERSION__?: string;
  __sparkDesktop?: DesktopBridge;
}
const w = (): DesktopWindow => (typeof window !== 'undefined' ? (window as DesktopWindow) : {});

export const isDesktopApp = (): boolean => !!w().__SPARK_DESKTOP__;
export const desktopVersion = (): string => w().__SPARK_DESKTOP_VERSION__ ?? w().__sparkDesktop?.version ?? '';

const AUTO_KEY = 'spark-desktop-auto';
const getAuto = (): boolean => {
  try {
    return localStorage.getItem(AUTO_KEY) !== '0';
  } catch {
    return true;
  }
};

type CheckState = 'idle' | 'checking' | 'latest';

export interface DesktopUpdates {
  isDesktop: boolean;
  version: string;
  auto: boolean;
  /** 'checking' right after a manual check; 'latest' if no relaunch happened within the grace window. */
  state: CheckState;
  setAuto: (on: boolean) => void;
  /** Manually check + install (relaunches if a newer build exists). */
  check: () => void;
}

export function useDesktopUpdates(): DesktopUpdates {
  const isDesktop = isDesktopApp();
  const [auto, setAutoState] = useState<boolean>(getAuto);
  const [state, setState] = useState<CheckState>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const setAuto = useCallback((on: boolean) => {
    setAutoState(on);
    try { localStorage.setItem(AUTO_KEY, on ? '1' : '0'); } catch { /* ignore */ }
    w().__sparkDesktop?.update?.('set-auto', on);
  }, []);

  const check = useCallback(() => {
    setState('checking');
    // 'now' FORCES the install if a newer build exists — a manual click means "update me now", and it
    // must NOT depend on the shell's persisted auto-update flag (which can drift out of sync with this
    // toggle and would otherwise make a found update silently no-op). 'check' relied on that flag.
    w().__sparkDesktop?.update?.('now');
    // No reverse channel: if a newer build exists the shell installs it and relaunches (this JS context
    // is torn down). If we're still alive after the grace window, we're already on the latest (or the
    // install failed — the shell shows a native notification in that case). Generous window so a slow
    // download+extract+swap isn't mistaken for "up to date".
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('latest'), 12000);
  }, []);

  return { isDesktop, version: desktopVersion(), auto, state, setAuto, check };
}
